import types from './types'
import { Axios, DOC_URL } from '@/helpers/axios'
import { parseVideo } from '@/helpers'

const convertDocumentResponse = data => {
    return data.reduce((acc, hit) => {
        hit._source.id = hit._id
        hit._source.name = hit._source.title

        if (!hit._source.type) hit._source.type = 'document'
        if (hit._source.type === 'document') hit._source.url = `${DOC_URL}${hit._source.filename}`
        return [...acc, hit._source]
    }, [])
}

const getAllAreas = ({ rootGetters, commit }) => {
    return new Promise((resolve, reject) => {
        Axios.get('/get-areas')
            .then(({ data }) => {
                if (data.status === 'success') {
                    let areas = data.areas
                    const allAreas = {
                        id: 0,
                        name: 'All Areas',
                        path: 'all-areas',
                        description: null,
                        keypoints: null,
                    }

                    if (rootGetters['Auth/isAdmin']) areas = [allAreas, ...data.areas]

                    commit(types.SET_AREAS, areas)
                    resolve(data.areas)
                }
                else throw new Error("Sorry, we can't retreive the common presentations at this time. Please try again later.")
            })
            .catch(err => reject(err))
    })
}

const addArea = (ctx, data) => {
    return new Promise ((resolve, reject) => {
        Axios.put('/add-area', { area: data })
            .then(res => resolve(res))
            .catch(err => reject(err))
    })
}

const updateArea = ({ state, commit }, data) => {
    return new Promise((resolve, reject) => {
        // Get the index of the area for mutation
        const areaInd = state.areas.findIndex(({ id }) => id === data.id)

        // Submit updates
        Axios.post('/edit-area', { area: data })
            .then(res => {
                commit(types.UPDATE_AREA, { data, areaInd })
                resolve(res)
            })
            .catch(err => reject(err))
    })
}

const buildGlobalFilters = ({ commit }, filters) => commit(types.BUILD_FILTERS, filters)
const setSearchFilters = ({ commit }, val) => commit(types.SET_SEARCH_FILTERS, val)

const addToZip = ({ commit }, file) => commit(types.ADD_TO_ZIP, file)
const removeFromZip = ({ commit }, id) => commit(types.REMOVE_FROM_ZIP, id)
const clearZipSelections = ({ commit }) => commit(types.CLEAR_ZIP_SELECTIONS)

const submitSearch = ({ commit }, keywords) => {
    commit(types.SET_LOADING, true)

    return new Promise((resolve, reject) => {
        Axios.post('/search', { search: { query: keywords, area: '', page: '' } })
            .then(({ data }) => {
                if (data.status === 'success') {
                    const documents = convertDocumentResponse(data.documents.hits.hits)
                    documents.forEach(doc => doc.group = doc.group.toLowerCase())
                    commit(types.SET_SEARCH_RESULTS, documents)
                    resolve(data)
                    return
                }

                throw new Error("Sorry, we can't seem to retrieve any results, please try again later.")
            })
            .catch(err => {
                commit(types.SET_SEARCH_ERROR, err.message || err)
                reject(err)
            })
            .finally(() => commit(types.SET_LOADING, false))
    })
}

const getAreaResources = ({ state, commit }, area) => {
    if (!state.areas) return

    const ind = state.areas.findIndex(item => item.name === area)
    return new Promise((resolve, reject) => {
        Axios.post('/search', { search: {
                query: '',
                area,
                page: ''
            } })
            .then(({ data }) => {
                if (data.status === 'success') {
                    const documents = convertDocumentResponse(data.documents.hits.hits)

                    commit(types.SET_AREA_RESOURCES, { ind, documents })
                    resolve(documents)
                    return
                }

                throw new Error("Sorry, we can't seem to get any resources, please try again later.")
            })
            .catch(err => {
                reject(err)
            })
    })
}

// Making a seperate function for pages instead of making Areas dynamic, to future proof
// page changes and avoid complexities & consequences of scaling.
const getPageResources = ({ rootState, commit }, { id, page, child }) => {
    const pageInd = rootState.App.pages.findIndex(item => item.path === `/${page}`)
    let childInd = -1

    if (child) {
        childInd = rootState.App.pages[pageInd].children
            .findIndex(item => item.path === `/${page}/${child}`)
    }

    return new Promise((resolve, reject) => {
        Axios.post('/search', { search: {
                query: '',
                area: '',
                page: id
            } })
            .then(({ data }) => {
                console.log(data, id)
                if (data.status === 'success') {
                    const documents = convertDocumentResponse(data.documents.hits.hits)

                    commit('App/SET_PAGE_RESOURCES', { pageInd, childInd, documents }, { root: true })
                    resolve(data)
                    return
                }

                throw new Error("Sorry, we can't seem to get any resources, please try again later.")
            })
            .catch(err => {
                reject(err)
            })
    })
}

const playVideo = ({ commit }, url) => commit(types.PLAY_VIDEO, parseVideo(url))
const closeVideo = ({ commit }) => commit(types.CLOSE_VIDEO)

const editResource = ({ commit }, resource) => {
    commit(types.SET_EDITING_RESOURCE, true)
    commit(types.SET_EDITING_RESOURCE_DATA, resource)
}

const updateResource = ({ rootState, state, commit }, data) => {
    let areaResource
    let searchResource
    const pageResource = {}

    // We check for each area a resource could be in, and see if it has an index value.
    // If it does, we update the LET variables, and pass those to the mutation to be updated.
    if (state.areas) {
        const areaIndex = state.areas.findIndex(({ name }) => name === data.area)

        if (areaIndex !== -1) {
            const resources = state.areas[areaIndex].resources
            const areaResourceIndex = resources && resources.findIndex(({ id }) => id === data.id)
            if (areaResourceIndex !== -1) areaResource = { areaIndex, areaResourceIndex }
        }
    }

    // check if there file is in search results
    if (state.searchResults && state.searchResults.length > 0) {
        const searchResourceIndex = state.searchResults.findIndex(({ id }) => id === data.id)
        if (searchResourceIndex !== -1) searchResource = searchResourceIndex
    }

    // check if it's on a page
    if (data.page) {
        const pageIndex = rootState.App.pages.findIndex(({ id }) => id === data.page)

        if (pageIndex !== -1) {
            const page = rootState.App.pages[pageIndex]
            const pageResources = page.resources
            const pageResourceIndex = pageResources && pageResources.findIndex(({ id }) => id === data.id)
            const hasResource = pageResourceIndex !== undefined && pageResourceIndex !== -1

            pageResource.pageIndex = pageIndex
            if (hasResource) pageResource.pageResourceIndex = pageResourceIndex

            if (data.childPage) {
                const childIndex = page.children.findIndex(({ path }) => path === data.childPage)
                const childResourceIndex = page.children[childIndex].resources && page.children[childIndex].resources.findIndex(({ id }) => id === data.id)
                const hasChildResource = childResourceIndex !== undefined && childResourceIndex !== -1

                pageResource.childIndex = childIndex
                if (hasChildResource) pageResource.childResourceIndex = childResourceIndex
            }

            commit('App/UPDATE_RESOURCE', { data, pageResource }, { root: true })
        }
    }

    // now let the mutation re-assign all the data
    commit(types.UPDATE_RESOURCE, { data, areaResource, searchResource })
}

const deleteGlobalResource = ({ rootState, state, commit }, {id, page, child}) => {
    const areas = []
    const searchResultIndex = state.searchResults.findIndex(result => result.id === id)

    state.areas.forEach((area, ind) => {
        if (area && !area.resources) return -1

        areas.push({area: ind, resource: area.resources.findIndex(res => res.id === id) })
    })

    if (page && page !== 'none') {
        const { pages } = rootState.App
        const pageInd = pages.findIndex(({ path }) => path === page)

        if (child && child !== 'none') {
            const childInd = pages[pageInd].children.findIndex(({ path }) => path === child)
            const resourceInd = pages[pageInd].children[childInd].resources.findIndex(r => r.id === id)

            commit('App/DELETE_RESOURCE', { pageInd, childInd, resourceInd }, {root: true})
            return
        }

        const resourceInd = pages[pageInd].resources.findIndex(r => r.id === id)
        commit('App/DELETE_RESOURCE', { pageInd, childInd: -1, resourceInd }, {root: true})
    }

    commit (types.DELETE_RESOURCE, { searchResultIndex, areas })
}

const closeEditingPanel = ({ commit }) => {
    commit(types.SET_EDITING_RESOURCE, false)
    commit(types.SET_EDITING_RESOURCE_DATA, null)
}

const unlockVuexTabs = ({ commit }) =>  commit(types.UNLOCK_TABS)

export default {
    convertDocumentResponse,
    getAllAreas,
    addArea,
    updateArea,
    buildGlobalFilters, // called from Filters.vue
    setSearchFilters,
    addToZip,
    removeFromZip,
    clearZipSelections,
    submitSearch,
    playVideo,
    closeVideo,
    editResource,
    updateResource,
    closeEditingPanel,
    unlockVuexTabs,
    getAreaResources,
    getPageResources,
    deleteGlobalResource
}
